import { Grid, makeStyles, Typography } from '@material-ui/core';
import React from 'react';
import PointerIcon from '../../../assets/Pointer.png';
import { COLORS } from '../../../plTheme';
import WidgetCard from '../../atoms/WidgetCard';
import { _t_ } from '../../../utils/translation/translation';
import { _n_ } from '../../../utils/numerals/numerals';
import { useGlobalStyles } from '../../../plStyles';
import { useWindowSize } from '../../../utils/hooks/useWindowSize';

interface ClosedSalesProps {
  closed?: number | null;
  existingClosed?: number | null;
  forecast?: number | null;
  reportInd: string;
}

const ClosedSales: React.FC<ClosedSalesProps> = ({
  closed,
  existingClosed,
  forecast,
  reportInd,
}: ClosedSalesProps) => {
  const [width] = useWindowSize();
  const indicator =
    forecast && closed && closed > forecast ? (forecast / closed) * 100 : 99.8;
  const colorGradientIndicator =
    forecast && closed && closed < forecast ? (closed / forecast) * 100 : 100;

  const useStyles = makeStyles({
    main: {
      flex: '22%',
      padding:
        reportInd == 'main'
          ? '19 0 0 16'
          : reportInd == 'report1'
          ? 17
          : '11 0 0 16',
    },
    col_sp: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-around',
    },
    row_sp: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    p_main: {
      padding: '2 16',
    },
    footer: {
      backgroundColor: COLORS.ALPHA_BACKGROUND_COLOR,
      width: '100%',
      height: reportInd == 'rep' ? '40' : '32',
      borderRadius: 4,
    },
    heading: {
      color: COLORS.TEXT_LOW_EMPHASIS,
      textTransform: 'uppercase',
    },
    bar: {
      height: '3.6px',
      width: '100%',
      backgroundColor: COLORS.MAIN_HOVER_LIGHT,
      borderRadius: 4,
      background:
        'linear-gradient(to right, ' +
        COLORS.PL_PRIMARY +
        ' ' +
        colorGradientIndicator.toString() +
        '%, ' +
        COLORS.MAIN_HOVER_LIGHT +
        ' 0%)',
    },
    pointer: {
      height: 17,
      marginTop: '-5px',
      marginLeft: `${indicator}%`,
      marginBottom: 3,
    },
    footerText: {
      marginTop: 6,
      color: COLORS.TEXT_LOW_EMPHASIS,
      textTransform: 'uppercase',
    },
    footerValue: {
      marginTop: 6,
      color: COLORS.TEXT_HIGH_EMPHASIS,
    },
    mainValue: {
      color: COLORS.TEXT_HIGH_EMPHASIS,
    },
    footerDiv: {
      height: '100%',
      padding: '0 18px',
    },
    existingValue: {
      fontWeight: 500,
      lineHeight: '18px',
      fontSize: '14px',
      color: COLORS.TEXT_HIGH_EMPHASIS,
    },
    existingValueText: {
      fontSize: '16px',
    },
  });

  const classes = useStyles();
  const globalClasses = useGlobalStyles();

  let newValue = existingClosed ? existingClosed : 0;
  newValue = closed ? closed - newValue : 0;

  return (
    <>
      <WidgetCard>
        <Grid
          container
          direction="column"
          justify="space-between"
          data-testid="TotalSalesColsed"
          style={{
            height: reportInd == 'main' ? 202 : reportInd == 'rep' ? 192 : 175,
            display: 'flex',
          }}
        >
          <Grid className={classes.main}>
            <Typography className={globalClasses.body1WidgetTitle}>
              {_t_('Total Sales Closed')}
            </Typography>
          </Grid>
          <Grid
            className={`${classes.p_main} ${classes.row_sp}`}
            style={{
              flex: '30%',
            }}
          >
            <Grid className={classes.col_sp}>
              <Grid>
                <Typography variant="h5" className={classes.existingValue}>
                  {/* For Rep Lens we uses 2 decimal value and others we user 1 decimal value */}
                  ${_n_(newValue, reportInd === 'rep' ? '0,0.0a' : '0,0.0a')}
                </Typography>
              </Grid>
              <Grid>
                <Typography variant="h5" className={classes.existingValue}>
                  {/* For Rep Lens we uses 2 decimal value and others we user 1 decimal value */}
                  $
                  {existingClosed &&
                    _n_(
                      existingClosed,
                      reportInd === 'rep' ? '0,0.0a' : '0,0.0a',
                    )}
                </Typography>
              </Grid>
            </Grid>
            <Grid className={classes.col_sp}>
              <Grid>
                <Typography variant="h5" className={classes.existingValueText}>
                  {` New `}
                </Typography>
              </Grid>
              <Grid>
                <Typography variant="h5" className={classes.existingValueText}>
                  {` Existing `}
                </Typography>
              </Grid>
            </Grid>
            <Grid style={{ alignContent: 'center' }}>
              <Typography
                variant={'h3'}
                className={classes.mainValue}
                data-testid="SalesClosedValue"
                align="right"
              >
                {/* For Rep Lens we uses 2 decimal value and others we user 1 decimal value */}
                $
                {closed && closed > 0
                  ? _n_(closed, reportInd === 'rep' ? '0,0.0a' : '0,0.0a')
                  : _n_(0.0, reportInd === 'rep' ? '0,0.0a' : '0,0.0a')}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            className={`${classes.p_main} ${classes.row_sp}`}
            style={{
              flex: '30%',
              alignItems: 'center',
            }}
          >
            <Grid className={classes.bar} data-testid="salesclosedPointer">
              {/* {closed && forecast && closed < forecast ? null : (
                  <img
                    data-testid="salesclosedimg"
                    src={PointerIcon}
                    className={classes.pointer}
                    alt="pointer"
                  />
                )} */}
              {closed && forecast && closed > forecast ? (
                <Typography
                  style={{
                    paddingTop: '4px',
                    display: 'flex',
                    justifyContent: 'end',
                  }}
                  variant="h6"
                >
                  {/* For Rep Lens we uses 2 decimal value and others we user 1 decimal value */}
                  {`+ $${_n_(
                    closed - forecast,
                    reportInd === 'rep' ? '0,0.00a' : '0,0.0a',
                  )}`}
                </Typography>
              ) : null}
            </Grid>
          </Grid>
          <Grid
            className={`${classes.p_main} ${classes.row_sp}`}
            style={{
              backgroundColor: '#d7dfe966',
              flex: '18%',
              paddingTop: 2,
            }}
          >
            <Typography variant="h6" className={classes.footerText}>
              {_t_('Quota')}
            </Typography>
            <Grid item>
              {forecast && forecast > 0 ? (
                <Typography
                  variant="h6"
                  className={classes.footerValue}
                  data-testid="foreCastValue"
                >
                  {/* For Rep Lens we uses 2 decimal value and others we user 1 decimal value */}
                  $
                  {forecast &&
                    _n_(
                      forecast.toFixed(1),
                      reportInd === 'rep' ? '0,0.00a' : '0,0.0a',
                    )}
                </Typography>
              ) : (
                <Typography
                  variant="h6"
                  className={classes.footerValue}
                  data-testid="foreCastValue"
                >
                  N/A
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </WidgetCard>
    </>
  );
};

export default ClosedSales;
